import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Clock, History, Paperclip, Users, Workflow as WorkflowIcon } from "lucide-react";
import type { ChatterCounts, FormButtonElement, RecordData } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useEvent } from "../react/hooks/useEvent";
import { useLocale } from "../react/providers/LocaleProvider";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";
import { fmtDateTime } from "../widgets/builtins";
import { uidLabel } from "./quickMetaHelpers";
import { FollowersPopover } from "./FollowersPopover";
import { RecordDocumentsDrawer } from "./RecordDocumentsDrawer";
import { WorkflowDrawer } from "./WorkflowDrawer";

// Record strip — FormView chrome between the control panel and the sheet
// (foundation.communication Enh 03 Slice 2; frozen mockup Option I).
//
// Chips render STRICTLY from the render plan's `activity: string[]` — the
// declared-AND-mixin-gated component list the backend ships (Slice 1). No
// component in the list → no chip; empty list (or a new unsaved record) →
// the strip doesn't render at all and the form looks exactly as before.
//
// Component → chip mapping (one chip per declared component):
//   Attachments          → 📎 Documents (deep-link to the Documents app
//                          filtered to this record: ?docCat=&docRec=)
//   ScheduledActivity    → 🕐 Activities (jumps to the timeline pre-filtered;
//                          graceful no-op until the timeline ships in S4)
//   Followers            → 👥 Followers (popover lands in S3)
//   MessageConversation  → 🕘 Timeline (jump, no count — the feed is paged)
//
// Split into a pure view (RecordStripView — unit-testable without providers)
// and a thin container (RecordStrip — wires runtime/locale/navigation hooks),
// mirroring the quickMetaHelpers extraction pattern.

export type TimelineFilter = "all" | "comment" | "change" | "file" | "task" | "email";

/** Documents-app deep link for one record — the Drive client action restores
    `?docCat=` / `?docRec=` on mount (storage DriveBrowser contract). */
export function buildDocumentsDeepLink(orgCode: string, modelKey: string, recordUuid: string): string {
    const qs = new URLSearchParams({ docCat: modelKey, docRec: recordUuid });
    return `/wc/${orgCode}/documents?${qs.toString()}`;
}

/** "Updated <when> by <who>" line for the strip's right edge; null when the
    record carries no update timestamp (e.g. still loading). */
export function buildUpdatedMeta(record: RecordData | null | undefined, timezone: string): string | null {
    const updatedAt = record?.updated_at_utc;
    if (typeof updatedAt !== "string") return null;
    const updatedBy = uidLabel(record?.updated_uid);
    return `Updated ${fmtDateTime(updatedAt, timezone)}${updatedBy !== "—" ? ` by ${updatedBy}` : ""}`;
}

interface RecordStripViewProps {
    /** Gated `activity` component list from the form render plan. */
    components: string[];
    counts: ChatterCounts | null;
    metaText: string | null;
    /** Toolbar variant — chips inline in the form control panel's middle
        slot (no padding, no meta; the (i) Quick Meta popover owns that). */
    toolbar?: boolean;
    /** When provided, rendered in place of the plain Followers chip — the
        container passes the FollowersPopover wrapping the chip as trigger. */
    followersSlot?: ReactNode;
    /** Workflow-driven record (model has a workflow=True field) — renders
        the ⑆ Workflow chip. Independent of the <activity> gating. */
    hasWorkflow?: boolean;
    onOpenWorkflow?: () => void;
    onOpenDocuments: () => void;
    onTimelineJump?: (filter: TimelineFilter) => void;
    onFollowersClick?: () => void;
}

export function RecordStripView({
    components,
    counts,
    metaText,
    toolbar,
    followersSlot,
    hasWorkflow,
    onOpenWorkflow,
    onOpenDocuments,
    onTimelineJump,
    onFollowersClick,
}: RecordStripViewProps): ReactNode {
    if (components.length === 0 && !hasWorkflow) return null;
    // Form-view revamp (Enh 14, Stage 4) — icon-only utility buttons with a
    // count badge (mockup `.rc-util--icon`); the label is the accessible
    // tooltip. Non-toolbar usages keep the label for clarity.
    const iconOnly = Boolean(toolbar);
    const lbl = (text: string) => (iconOnly ? null : text);
    // Count badge. Documents + Followers always show their count in the toolbar
    // (muted zero state), mirroring the mockup's rc-util__count / --zero;
    // everywhere else the badge only appears when non-zero.
    const countChip = (n: number | undefined, always = false): ReactNode => {
        if (!always && !n) return null;
        return <span className={`record-strip__count${n ? "" : " record-strip__count--zero"}`}>{n ?? 0}</span>;
    };
    return (
        <div className={`record-strip${toolbar ? " record-strip--toolbar" : ""}`} data-testid="record-strip">
            {components.includes("Attachments") ? (
                <button
                    type="button"
                    className={`record-strip__chip${iconOnly ? " record-strip__chip--icon" : ""}`}
                    onClick={onOpenDocuments}
                    title="Documents"
                >
                    <Paperclip aria-hidden="true" />
                    {lbl("Documents")}
                    {countChip(counts?.attachments, iconOnly)}
                </button>
            ) : null}
            {components.includes("ScheduledActivity") ? (
                <button
                    type="button"
                    className={`record-strip__chip${iconOnly ? " record-strip__chip--icon" : ""}`}
                    onClick={() => onTimelineJump?.("task")}
                    title="Activities"
                >
                    <Clock aria-hidden="true" />
                    {lbl("Activities")}
                    {countChip(counts?.openActivities)}
                </button>
            ) : null}
            {components.includes("Followers") ? (
                followersSlot ?? (
                    <button
                        type="button"
                        className={`record-strip__chip${iconOnly ? " record-strip__chip--icon" : ""}`}
                        onClick={() => onFollowersClick?.()}
                        title="Followers"
                    >
                        <Users aria-hidden="true" />
                        {lbl("Followers")}
                        {countChip(counts?.followers, iconOnly)}
                    </button>
                )
            ) : null}
            {components.includes("MessageConversation") ? (
                <button
                    type="button"
                    className={`record-strip__chip${iconOnly ? " record-strip__chip--icon" : ""}`}
                    onClick={() => onTimelineJump?.("all")}
                    title="Timeline"
                >
                    <History aria-hidden="true" />
                    {lbl("Timeline")}
                </button>
            ) : null}
            {hasWorkflow ? (
                <button
                    type="button"
                    className={`record-strip__chip${iconOnly ? " record-strip__chip--icon" : ""}`}
                    onClick={() => onOpenWorkflow?.()}
                    title="Workflow"
                >
                    <WorkflowIcon aria-hidden="true" />
                    {lbl("Workflow")}
                </button>
            ) : null}
            {metaText ? <span className="record-strip__meta">{metaText}</span> : null}
        </div>
    );
}

interface RecordStripProps {
    components: string[];
    modelKey: string;
    /** Undefined for a new unsaved record — the strip hides itself. */
    recordUuid?: string;
    /** Loaded record — drives the "Updated … by …" meta line (row variant). */
    record?: RecordData | null;
    /** Record display title — drawer header ("Documents — <title>"). */
    recordTitle?: string;
    /** Toolbar variant — inline chips, no meta line. */
    toolbar?: boolean;
    /** Workflow-driven record — renders the ⑆ Workflow chip + drawer. */
    hasWorkflow?: boolean;
    /** Workflow drawer footer next-step buttons (Enh 14 Stage 4). */
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
    actionSlug?: string;
    /** External epoch (composer posts etc.) — refetches the chip counts. */
    countsRefresh?: number;
    /** S4 wires this to the timeline; absent → chips that target it no-op. */
    onTimelineJump?: (filter: TimelineFilter) => void;
    /** S3 wires this to the followers popover; absent → chip no-ops. */
    onFollowersClick?: () => void;
    /** Fired on any chatter mutation (upload/delete/follow/…) so siblings
        (e.g. the timeline) can refetch. */
    onMutated?: () => void;
}

export function RecordStrip({
    components,
    modelKey,
    recordUuid,
    record,
    recordTitle,
    toolbar,
    hasWorkflow,
    headerButtons,
    onRunButton,
    onRunTransition,
    actionSlug,
    countsRefresh,
    onTimelineJump,
    onFollowersClick,
    onMutated,
}: RecordStripProps): ReactNode {
    const runtime = useRuntime();
    const locale = useLocale();
    const { orgCode, navigateTo } = useWorkspaceNavigation();
    const [counts, setCounts] = useState<ChatterCounts | null>(null);
    const [docsOpen, setDocsOpen] = useState(false);
    const [workflowOpen, setWorkflowOpen] = useState(false);
    // Bumped by drawer mutations (upload/delete) to refetch the chip counts.
    const [countsEpoch, setCountsEpoch] = useState(0);

    // The cockpit <status> card's "View full lifecycle" link opens the same
    // workflow drawer (owned here) via the event bus.
    useEvent("cockpit.open_workflow", () => setWorkflowOpen(true));

    useEffect(() => {
        if (!recordUuid || components.length === 0) return;
        let active = true;
        runtime.services.chatter
            .counts(modelKey, recordUuid)
            .then((c) => {
                if (active) setCounts(c);
            })
            .catch(() => {
                /* chips render without numbers — counts are decoration */
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelKey, recordUuid, countsEpoch, countsRefresh]);

    if (!recordUuid) return null;

    return (
        <>
            <RecordStripView
                components={components}
                counts={counts}
                metaText={toolbar ? null : buildUpdatedMeta(record, locale.timezone)}
                toolbar={toolbar}
                hasWorkflow={hasWorkflow}
                onOpenWorkflow={() => setWorkflowOpen(true)}
                followersSlot={
                    <FollowersPopover
                        modelKey={modelKey}
                        recordUuid={recordUuid}
                        onChanged={() => {
                            setCountsEpoch((n) => n + 1);
                            onMutated?.();
                        }}
                        trigger={
                            <button
                                type="button"
                                className={`record-strip__chip${toolbar ? " record-strip__chip--icon" : ""}`}
                                title="Followers"
                            >
                                <Users aria-hidden="true" />
                                {toolbar ? null : "Followers"}
                                {toolbar
                                    ? <span className={`record-strip__count${counts?.followers ? "" : " record-strip__count--zero"}`}>{counts?.followers ?? 0}</span>
                                    : counts && counts.followers
                                      ? <span className="record-strip__count">{counts.followers}</span>
                                      : null}
                            </button>
                        }
                    />
                }
                onOpenDocuments={() => setDocsOpen(true)}
                onTimelineJump={onTimelineJump}
                onFollowersClick={onFollowersClick}
            />
            {/* In-place documents drawer — no navigation; the Documents app
                deep link only appears inside it when the model is documents-
                app-visible. */}
            <RecordDocumentsDrawer
                open={docsOpen}
                onOpenChange={setDocsOpen}
                modelKey={modelKey}
                recordUuid={recordUuid}
                title={recordTitle ?? modelKey}
                onOpenInApp={() => {
                    setDocsOpen(false);
                    navigateTo(buildDocumentsDeepLink(orgCode, modelKey, recordUuid));
                }}
                onChanged={() => {
                    setCountsEpoch((n) => n + 1);
                    onMutated?.();
                }}
            />
            {hasWorkflow ? (
                <WorkflowDrawer
                    open={workflowOpen}
                    onOpenChange={setWorkflowOpen}
                    modelKey={modelKey}
                    recordUuid={recordUuid}
                    title={recordTitle ?? modelKey}
                    headerButtons={headerButtons}
                    onRunButton={onRunButton}
                    onRunTransition={onRunTransition}
                    actionSlug={actionSlug}
                />
            ) : null}
        </>
    );
}
